import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, useSearchParams, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Calendar as CalendarPicker } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Progress } from "@/components/ui/progress";
import { 
  ShoppingBag, Send, Link2, Users, Calendar, Clock,
  ArrowLeft, MessageSquare, ThumbsUp, Plus, X, ExternalLink, LogOut, Gift,
  Crown, UserPlus, Pencil, Shield, ChevronDown, Trash2, Wallet, Lightbulb, DollarSign, CreditCard
} from "lucide-react";
import { FundingProgressHeader } from "@/components/FundingProgressHeader";
import { UpiQrCode } from "@/components/UpiQrCode";
import { RazorpayCheckoutButton } from "@/components/RazorpayCheckoutButton";
import { Textarea } from "@/components/ui/textarea";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { format } from "date-fns";
import { toast } from "sonner";
import { SEO } from "@/components/SEO";
import { CustomerHeader } from "@/components/CustomerHeader";
import { CustomerFooter } from "@/components/CustomerFooter";
import { SocialShareSection } from "@/components/SocialShareSection";
import { validateEmail } from "@/lib/emailValidation";
import { formatCurrency, Currency } from "@/lib/currency";
import { InviteLinkCopyButton } from "@/components/InviteLinkCopyButton";
import { DiscussionChat } from "@/components/discussion/DiscussionChat";
import { DiscussionIdeas } from "@/components/discussion/DiscussionIdeas";
import { DiscussionBudget } from "@/components/discussion/DiscussionBudget";
import { DiscussionPeople } from "@/components/discussion/DiscussionPeople";
import { DiscussionActionBar } from "@/components/discussion/DiscussionActionBar";
import { GroupPaymentConfig } from "@/components/GroupPaymentConfig";
import { AddGroupPaymentDialog } from "@/components/AddGroupPaymentDialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface GroupPurchase {
  id: string;
  title: string;
  description: string | null;
  organizer_email: string;
  organizer_name: string | null;
  beneficiary_name: string | null;
  target_date: string | null;
  target_budget_min: number | null;
  target_budget_max: number | null;
  status: string;
  created_at: string;
  // Payment fields
  payment_type: string;
  split_type: string;
  target_amount: number | null;
  collected_amount: number;
  currency: string;
  payment_link: string | null;
  organizer_upi_id: string | null;
  merchant_id: string | null;
  // Razorpay Route fields
  razorpay_linked_account_id: string | null;
  platform_fee_per_participant: number | null;
  payment_recipient_type: string | null;
  // Invite link fields
  invite_link_enabled?: boolean;
  invite_link_token?: string;
  invite_link_approval_mode?: string;
  max_self_invites?: number | null;
}

interface Participant {
  id: string;
  email: string;
  name: string | null;
  invited_by: string;
  status: string;
  role: string;
  // Payment fields
  has_paid?: boolean;
  contribution_amount?: number | null;
  paid_at?: string | null;
  payment_method?: string | null;
}

interface Message {
  id: string;
  sender_email: string;
  sender_name: string | null;
  message: string;
  message_type: string;
  link_url: string | null;
  link_title: string | null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  votes: any[];
  vote_count: number;
  created_at: string;
}


export default function PurchaseDiscussion() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [purchase, setPurchase] = useState<GroupPurchase | null>(null);
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(true);
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [userName, setUserName] = useState<string>("");
  const [verificationStep, setVerificationStep] = useState<"verify" | "verified">("verify");
  const [emailInput, setEmailInput] = useState("");
  const [showLeaveDialog, setShowLeaveDialog] = useState(false);
  const [isLeaving, setIsLeaving] = useState(false);
  
  // Chat state
  const [newMessage, setNewMessage] = useState("");
  const [sendingMessage, setSendingMessage] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  
  // Link sharing state (kept for chat link sharing)
  const [showLinkForm, setShowLinkForm] = useState(false);
  const [linkUrl, setLinkUrl] = useState("");
  const [linkTitle, setLinkTitle] = useState("");
  
  // Invite state
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteName, setInviteName] = useState("");
  
  // Edit date state (for organizers/admins)
  const [editDateOpen, setEditDateOpen] = useState(false);
  const [editingDate, setEditingDate] = useState<Date | undefined>();
  const [isSavingDate, setIsSavingDate] = useState(false);
  
  // Edit title/description state (for organizers/admins)
  const [editTitleOpen, setEditTitleOpen] = useState(false);
  const [editingTitle, setEditingTitle] = useState("");
  const [editingDescription, setEditingDescription] = useState("");
  const [isSavingTitle, setIsSavingTitle] = useState(false);
  
  // Close event state (organizer only)
  const [showCloseDialog, setShowCloseDialog] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [closeMessage, setCloseMessage] = useState("");
  const [collectPaymentDialogOpen, setCollectPaymentDialogOpen] = useState(false);

  // Handle token-based auto-verification from email links
  useEffect(() => {
    const token = searchParams.get('token');
    if (!token || !id || verificationStep === "verified") return;

    const verifyByToken = async () => {
      try {
        // Check if token matches any participant
        const { data: participant, error } = await supabase
          .from("group_purchase_participants")
          .select("email, name, status")
          .eq("purchase_id", id)
          .eq("access_token", token)
          .single();

        if (error || !participant) {
          console.log("Token verification failed:", error);
          return;
        }

        if (participant.status !== 'active') {
          toast.error("Your participation has been cancelled");
          return;
        }

        // Token valid - auto-login
        const session = {
          email: participant.email.toLowerCase(),
          method: "email" as const,
        };
        sessionStorage.setItem("shakatah_customer_session", JSON.stringify(session));
        
        setUserEmail(participant.email.toLowerCase());
        setUserName(participant.name || participant.email.split('@')[0]);
        setVerificationStep("verified");
        fetchMessages(true);
        toast.success("Welcome! You've been verified via email link.");
        
        // Clean up URL
        navigate(`/purchases/${id}`, { replace: true });
      } catch (err) {
        console.error("Token verification error:", err);
      }
    };

    verifyByToken();
  }, [searchParams, id, verificationStep]);

  // Handle opt-out from email link
  useEffect(() => {
    const action = searchParams.get('action');
    const email = searchParams.get('email');
    
    if (action === 'opt-out' && email && id) {
      handleOptOut(email);
    }
  }, [searchParams, id]);

  const handleOptOut = async (email: string) => {
    try {
      const { error } = await supabase
        .from("group_purchase_participants")
        .update({ status: 'opted_out' })
        .eq("purchase_id", id)
        .eq("email", email.toLowerCase());

      if (error) throw error;
      
      toast.success("You've opted out of this group purchase");
      navigate("/my-purchases");
    } catch (error) {
      console.error("Error opting out:", error);
      toast.error("Failed to opt out. Please try again.");
    }
  };

  const handleLeavePurchase = async () => {
    if (!userEmail || !id) return;
    
    setIsLeaving(true);
    try {
      const isOrganizer = purchase?.organizer_email.toLowerCase() === userEmail.toLowerCase();
      
      if (isOrganizer) {
        toast.error("As the organizer, you cannot leave this purchase");
        return;
      }

      const { error } = await supabase
        .from("group_purchase_participants")
        .update({ status: 'opted_out' })
        .eq("purchase_id", id)
        .eq("email", userEmail.toLowerCase());

      if (error) throw error;
      
      // Notify organizer
      try {
        await supabase.functions.invoke("notify-participant-left", {
          body: {
            purchaseId: id,
            participantName: userName,
            participantEmail: userEmail,
          },
        });
      } catch (notifyError) {
        console.error("Error notifying organizer:", notifyError);
      }
      
      toast.success("You've left this group purchase");
      navigate("/my-purchases");
    } catch (error) {
      console.error("Error leaving purchase:", error);
      toast.error("Failed to leave purchase");
    } finally {
      setIsLeaving(false);
      setShowLeaveDialog(false);
    }
  };

  // Handle saving edited date (organizers only)
  const handleSaveDate = async () => {
    if (!id || !editingDate) return;
    
    setIsSavingDate(true);
    try {
      const { error } = await supabase
        .from("group_purchases")
        .update({ target_date: format(editingDate, "yyyy-MM-dd") })
        .eq("id", id);

      if (error) throw error;
      
      // Update local state
      if (purchase) {
        setPurchase({ ...purchase, target_date: format(editingDate, "yyyy-MM-dd") });
      }
      
      toast.success("Target date updated successfully");
      setEditDateOpen(false);
    } catch (error) {
      console.error("Error updating date:", error);
      toast.error("Failed to update date");
    } finally {
      setIsSavingDate(false);
    }
  };

  // Handle saving edited title/description (organizers/admins)
  const handleSaveTitle = async () => {
    if (!id || !editingTitle.trim()) return;
    
    setIsSavingTitle(true);
    try {
      const { error } = await supabase
        .from("group_purchases")
        .update({ 
          title: editingTitle.trim(),
          description: editingDescription.trim() || null
        })
        .eq("id", id);

      if (error) throw error;
      
      // Update local state
      if (purchase) {
        setPurchase({ 
          ...purchase, 
          title: editingTitle.trim(),
          description: editingDescription.trim() || null
        });
      }
      
      toast.success("Purchase details updated successfully");
      setEditTitleOpen(false);
    } catch (error) {
      console.error("Error updating title:", error);
      toast.error("Failed to update purchase details");
    } finally {
      setIsSavingTitle(false);
    }
  };

  // Handle promoting participant to admin (organizers only)
  const handlePromoteToAdmin = async (participantId: string, email: string, name?: string | null) => {
    if (!id || !purchase) return;
    
    try {
      const { error } = await supabase
        .from("group_purchase_participants")
        .update({ role: 'admin' })
        .eq("id", participantId);

      if (error) throw error;
      
      toast.success(`${name || email.split('@')[0]} is now an admin`);
      
      // Send notification
      try {
        await supabase.functions.invoke('notify-admin-promotion', {
          body: {
            promotedEmail: email,
            promotedName: name,
            promotedByEmail: userEmail || '',
            eventType: 'group_purchase',
            eventId: id,
            eventTitle: purchase.title,
            beneficiaryName: purchase.organizer_name,
            targetAmount: purchase.target_amount,
            currency: purchase.currency || 'INR',
          }
        });
      } catch (notifyError) {
        console.error('Error sending promotion notification:', notifyError);
      }
      
      fetchPurchaseData();
    } catch (error) {
      console.error("Error promoting to admin:", error);
      toast.error("Failed to promote participant");
    }
  };

  // Handle demoting admin to participant (organizers only)
  const handleDemoteToParticipant = async (participantId: string, email: string) => {
    if (!id) return;
    
    try {
      const { error } = await supabase
        .from("group_purchase_participants")
        .update({ role: 'participant' })
        .eq("id", participantId);

      if (error) throw error;
      
      toast.success(`${email.split('@')[0]} is now a participant`);
      fetchPurchaseData();
    } catch (error) {
      console.error("Error demoting participant:", error);
      toast.error("Failed to change role");
    }
  };

  // Remove participant (organizers and admins only)
  const handleRemoveParticipant = async (participantId: string, email: string, name?: string | null) => {
    if (!id) return;
    
    try {
      // Update status to 'removed' instead of deleting to keep history
      const { error } = await supabase
        .from("group_purchase_participants")
        .update({ status: 'removed' })
        .eq("id", participantId);

      if (error) throw error;
      
      // Notify the removed participant
      try {
        await supabase.functions.invoke("notify-participant-removed", {
          body: {
            participantEmail: email,
            participantName: name || null,
            eventName: purchase?.title || 'Group Purchase',
            eventType: 'purchase',
            organizerName: userName || purchase?.organizer_name || null,
            organizerEmail: userEmail
          }
        });
      } catch (notifyError) {
        console.error("Failed to notify removed participant:", notifyError);
      }
      
      toast.success(`${name || email.split('@')[0]} has been removed`);
      fetchPurchaseData();
    } catch (error) {
      console.error("Error removing participant:", error);
      toast.error("Failed to remove participant");
    }
  };

  // Cancel purchase (organizer only)
  const handleCloseEvent = async () => {
    if (!id || !userEmail || !purchase) return;
    
    const isOrg = purchase.organizer_email.toLowerCase() === userEmail.toLowerCase();
    const isAdmin = participants.find(p => p.email.toLowerCase() === userEmail.toLowerCase())?.role === 'admin';
    if (!isOrg && !isAdmin) {
      toast.error("Only the organizer or admin can close this event");
      return;
    }
    
    setIsClosing(true);
    try {
      // Collect active participant emails before closing
      const activeParticipants = participants.filter(p => p.status === 'active');
      const participantsToNotify = activeParticipants.map(p => ({
        email: p.email,
        name: p.name
      }));

      // Send closure notification emails to all participants
      if (participantsToNotify.length > 0) {
        try {
          await supabase.functions.invoke("notify-event-closed", {
            body: {
              eventId: id,
              eventName: purchase.title,
              eventType: 'group_payment',
              organizerName: purchase.organizer_name || userEmail.split('@')[0],
              organizerEmail: userEmail,
              participants: participantsToNotify,
              message: closeMessage.trim() || undefined,
              collectedAmount: purchase.collected_amount || 0,
              targetAmount: purchase.target_amount || 0,
              currency: purchase.currency || 'INR',
            },
          });
        } catch (emailError) {
          console.error("Error sending closure emails:", emailError);
          // Continue with closing even if emails fail
        }
      }
      
      // Reset close message
      setCloseMessage("");

      const { error } = await supabase
        .from("group_purchases")
        .update({ status: 'closed', completed_at: new Date().toISOString() })
        .eq("id", id);

      if (error) throw error;
      
      toast.success("Event closed successfully. All participants have been notified.");
      navigate("/my-purchases");
    } catch (error) {
      console.error("Error closing event:", error);
      toast.error("Failed to close event");
    } finally {
      setIsClosing(false);
      setShowCloseDialog(false);
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem("shakatah_customer_session");
    sessionStorage.removeItem("shakatah_gift_session");
    navigate("/my-purchases");
  };

  // Auto-verify from session on mount
  useEffect(() => {
    if (id) {
      fetchPurchaseData();
    }
  }, [id]);

  // Check for existing session after purchase data is loaded
  useEffect(() => {
    if (!purchase || verificationStep === "verified") return;

    // Check for existing session tokens in both localStorage and sessionStorage
    const sessionKeys = ['shakatah_customer_session', 'shakatah_gift_session'];
    const storages = [sessionStorage, localStorage];
    
    for (const storage of storages) {
      for (const key of sessionKeys) {
        try {
          const sessionData = storage.getItem(key);
          if (sessionData) {
            const parsed = JSON.parse(sessionData);
            const storedEmail = parsed.email?.toLowerCase?.() || parsed.verifiedEmail?.toLowerCase?.();
            
            console.log("Checking session:", { key, storedEmail, organizer: purchase.organizer_email });
            
            if (storedEmail) {
              // Check if this email is organizer (organizers are NOT in participants array)
              const isOrganizer = purchase.organizer_email.toLowerCase() === storedEmail;
              // Check if active participant
              const isParticipant = participants.some(p => 
                p.email.toLowerCase() === storedEmail && p.status === 'active'
              );
              
              console.log("Verification check:", { isOrganizer, isParticipant });
              
              if (isOrganizer || isParticipant) {
                setUserEmail(storedEmail);
                setUserName(
                  isOrganizer 
                    ? (purchase.organizer_name || storedEmail.split('@')[0])
                    : (participants.find(p => p.email.toLowerCase() === storedEmail)?.name || storedEmail.split('@')[0])
                );
                setVerificationStep("verified");
                fetchMessages(true);
                return;
              }
            }
          }
        } catch (e) {
          console.error("Session parse error:", e);
        }
      }
    }
  }, [purchase, participants, verificationStep]);

  useEffect(() => {
    if (!id || verificationStep !== "verified") return;

    // Set up realtime subscription for messages
    const channel = supabase
      .channel(`purchase-${id}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'group_purchase_discussions',
          filter: `purchase_id=eq.${id}`,
        },
        () => {
          fetchMessages();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [id, verificationStep]);

  const previousMessageCount = useRef<number>(0);

  // Only scroll to bottom when NEW messages arrive (not on initial load)
  useEffect(() => {
    if (previousMessageCount.current > 0 && messages.length > previousMessageCount.current) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
    previousMessageCount.current = messages.length;
  }, [messages]);

  const fetchPurchaseData = async () => {
    if (!id) return;
    
    try {
      const { data: purchaseData, error } = await supabase
        .from("group_purchases")
        .select("*")
        .eq("id", id)
        .single();

      if (error) throw error;
      setPurchase(purchaseData);

      // Fetch ALL participants (including opted-out for history)
      const { data: participantsData } = await supabase
        .from("group_purchase_participants")
        .select("*")
        .eq("purchase_id", id);

      setParticipants(participantsData || []);
    } catch (error) {
      console.error("Error fetching purchase:", error);
      toast.error("Failed to load purchase details");
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async (isInitialLoad = false) => {
    if (!id) return;
    
    const { data, error } = await supabase
      .from("group_purchase_discussions")
      .select("*")
      .eq("purchase_id", id)
      .order("created_at", { ascending: true });

    if (!error && data) {
      // Map data to ensure votes is always an array
      const mappedMessages: Message[] = data.map(m => ({
        id: m.id,
        sender_email: m.sender_email,
        sender_name: m.sender_name,
        message: m.message,
        message_type: m.message_type,
        link_url: m.link_url,
        link_title: m.link_title,
        votes: Array.isArray(m.votes) ? m.votes : [],
        vote_count: m.vote_count || 0,
        created_at: m.created_at,
      }));
      
      // For initial load, set the count first to prevent auto-scroll
      if (isInitialLoad) {
        previousMessageCount.current = mappedMessages.length;
      }
      
      setMessages(mappedMessages);
    }
  };


  const handleVerifyEmail = () => {
    if (!emailInput.trim()) {
      toast.error("Please enter your email");
      return;
    }

    const emailLower = emailInput.toLowerCase().trim();
    
    // Check if user is organizer or active participant
    const isOrganizer = purchase?.organizer_email.toLowerCase() === emailLower;
    const isParticipant = participants.some(p => 
      p.email.toLowerCase() === emailLower && p.status === 'active'
    );

    if (!isOrganizer && !isParticipant) {
      toast.error("You're not a participant in this purchase");
      return;
    }

    // Store session so user stays logged in when navigating to My Purchases
    const session = {
      email: emailLower,
      method: "email" as const,
    };
    sessionStorage.setItem("shakatah_customer_session", JSON.stringify(session));

    setUserEmail(emailLower);
    setUserName(
      isOrganizer 
        ? (purchase?.organizer_name || emailLower.split('@')[0])
        : (participants.find(p => p.email.toLowerCase() === emailLower)?.name || emailLower.split('@')[0])
    );
    setVerificationStep("verified");
    fetchMessages(true);
    toast.success("Welcome to the discussion!");
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !userEmail || !id) return;

    setSendingMessage(true);
    try {
      const { error } = await supabase
        .from("group_purchase_discussions")
        .insert({
          purchase_id: id,
          sender_email: userEmail,
          sender_name: userName,
          message: newMessage.trim(),
          message_type: "text",
        });

      if (error) throw error;
      setNewMessage("");
    } catch (error) {
      console.error("Error sending message:", error);
      toast.error("Failed to send message");
    } finally {
      setSendingMessage(false);
    }
  };

  const handleShareLink = async () => {
    if (!linkUrl.trim() || !userEmail || !id) return;

    try {
      const { error } = await supabase
        .from("group_purchase_discussions")
        .insert({
          purchase_id: id,
          sender_email: userEmail,
          sender_name: userName,
          message: linkTitle.trim() || "Shared a product link",
          message_type: "product_link",
          link_url: linkUrl.trim(),
          link_title: linkTitle.trim() || null,
        });

      if (error) throw error;
      setLinkUrl("");
      setLinkTitle("");
      setShowLinkForm(false);
      toast.success("Link shared!");
    } catch (error) {
      console.error("Error sharing link:", error);
      toast.error("Failed to share link");
    }
  };

  const handleVote = async (messageId: string) => {
    if (!userEmail) return;

    const message = messages.find(m => m.id === messageId);
    if (!message) return;

    const votes = message.votes || [];
    const hasVoted = votes.includes(userEmail);

    const newVotes = hasVoted 
      ? votes.filter((v: string) => v !== userEmail)
      : [...votes, userEmail];

    try {
      const { error } = await supabase
        .from("group_purchase_discussions")
        .update({ 
          votes: newVotes,
          vote_count: newVotes.length 
        })
        .eq("id", messageId);

      if (error) throw error;
      fetchMessages();
    } catch (error) {
      console.error("Error voting:", error);
    }
  };

  const handleInviteFriend = async () => {
    const email = inviteEmail.trim();
    if (!email || !userEmail || !id) return;

    // Comprehensive email validation
    const validation = validateEmail(email);
    if (!validation.valid) {
      toast.error(validation.error || "Please enter a valid email address");
      return;
    }

    try {
      const { error } = await supabase
        .from("group_purchase_participants")
        .insert({
          purchase_id: id,
          email: inviteEmail.trim().toLowerCase(),
          name: inviteName.trim() || null,
          invited_by: userEmail,
        });

      if (error) {
        if (error.code === '23505') {
          toast.error("This person is already invited");
        } else {
          throw error;
        }
        return;
      }

      toast.success("Friend invited!");
      setInviteEmail("");
      setInviteName("");
      setShowInviteForm(false);
      fetchPurchaseData();
    } catch (error) {
      console.error("Error inviting friend:", error);
      toast.error("Failed to invite friend");
    }
  };


  if (loading) {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        <CustomerHeader userEmail={userEmail || undefined} />
        <div className="flex-1 flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-customer"></div>
        </div>
        <CustomerFooter />
      </div>
    );
  }

  if (!purchase) {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        <CustomerHeader userEmail={userEmail || undefined} />
        <div className="flex-1 flex items-center justify-center py-20">
          <Card className="p-8 text-center">
            <ShoppingBag className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
            <h2 className="text-xl font-semibold">Purchase not found</h2>
            <Button onClick={() => navigate("/my-purchases")} className="mt-4">
              Go to Dashboard
            </Button>
          </Card>
        </div>
        <CustomerFooter />
      </div>
    );
  }

  if (verificationStep === "verify") {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        <CustomerHeader userEmail={userEmail || undefined} />
        <SEO title={`${purchase.title} - Group Purchase`} />
        <div className="flex-1 bg-gradient-to-b from-customer/5 to-background flex items-center justify-center p-4 py-20">
          <Card className="w-full max-w-md">
            <CardHeader className="text-center">
              <div className="w-16 h-16 rounded-full bg-customer/10 flex items-center justify-center mx-auto mb-4">
                <ShoppingBag className="w-8 h-8 text-customer" />
              </div>
              <CardTitle>{purchase.title}</CardTitle>
              <p className="text-sm text-muted-foreground">
                Organized by {purchase.organizer_name || purchase.organizer_email.split('@')[0]}
              </p>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Enter your email to join</label>
                <Input
                  type="email"
                  placeholder="your@email.com"
                  value={emailInput}
                  onChange={(e) => setEmailInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleVerifyEmail()}
                />
              </div>
              <Button onClick={handleVerifyEmail} className="w-full bg-customer hover:bg-customer/90">
                Join Discussion
              </Button>
            </CardContent>
          </Card>
        </div>
        <CustomerFooter />
      </div>
    );
  }

  

  const isOrganizer = purchase.organizer_email.toLowerCase() === userEmail?.toLowerCase();
  const isAdmin = participants.some(p => 
    p.email.toLowerCase() === userEmail?.toLowerCase() && p.role === 'admin' && p.status === 'active'
  );
  const canEdit = isOrganizer || isAdmin;
  
  const daysUntil = purchase.target_date 
    ? Math.ceil((new Date(purchase.target_date).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24))
    : null;

  // Determine if this is a Group Payment or Group Purchase using the explicit category field
  const isGroupPayment = (purchase as any).category === 'payment';
  
  // Theme colors based on type: Purple for Purchases, Emerald for Payments
  const headerGradient = isGroupPayment 
    ? "bg-gradient-to-r from-emerald-500 via-teal-500 to-emerald-600"
    : "bg-gradient-to-r from-violet-600 via-purple-500 to-violet-600";
  const badgeLabel = isGroupPayment ? "Group Payment" : "Group Purchase";

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <CustomerHeader userEmail={userEmail || undefined} onLogout={handleLogout} />
      <SEO title={`${purchase.title} - ${badgeLabel}`} />
      
      <div className="flex-1 container pt-16 pb-20 max-w-4xl">
        {/* Back button */}
        <Link to="/my-purchases" className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground mb-4">
          <ArrowLeft className="w-4 h-4 mr-1" />
          My Dashboard
        </Link>

        {/* Purchase Header Card - Purple for Purchases, Emerald for Payments */}
        <Card className="mb-6 overflow-hidden">
          <div className={`${headerGradient} p-6 text-white`}>
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <Badge className="bg-white/20 text-white mb-2">
                  {isGroupPayment ? <Wallet className="w-3 h-3 mr-1" /> : <ShoppingBag className="w-3 h-3 mr-1" />}
                  {badgeLabel}
                </Badge>
                <div className="flex items-center gap-2">
                  <h1 className="text-2xl font-bold">{purchase.title}</h1>
                  {canEdit && (
                    <Popover open={editTitleOpen} onOpenChange={setEditTitleOpen}>
                      <PopoverTrigger asChild>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="h-6 w-6 p-0 text-white/70 hover:text-white hover:bg-white/10"
                          onClick={() => {
                            setEditingTitle(purchase.title);
                            setEditingDescription(purchase.description || "");
                          }}
                        >
                          <Pencil className="w-3 h-3" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-80" align="start">
                        <div className="space-y-3">
                          <p className="text-sm font-medium">Edit purchase details</p>
                          <div className="space-y-2">
                            <Input
                              placeholder="Title"
                              value={editingTitle}
                              onChange={(e) => setEditingTitle(e.target.value)}
                            />
                            <Textarea
                              placeholder="Description (optional)"
                              value={editingDescription}
                              onChange={(e) => setEditingDescription(e.target.value)}
                              rows={3}
                            />
                          </div>
                          <div className="flex gap-2">
                            <Button 
                              size="sm" 
                              variant="outline" 
                              className="flex-1"
                              onClick={() => setEditTitleOpen(false)}
                            >
                              Cancel
                            </Button>
                            <Button 
                              size="sm" 
                              className="flex-1 bg-customer hover:bg-customer/90"
                              onClick={handleSaveTitle}
                              disabled={isSavingTitle || !editingTitle.trim()}
                            >
                              {isSavingTitle ? "Saving..." : "Save"}
                            </Button>
                          </div>
                        </div>
                      </PopoverContent>
                    </Popover>
                  )}
                </div>
                {purchase.description && (
                  <p className="text-white/80 mt-1">{purchase.description}</p>
                )}
                {purchase.beneficiary_name && (
                  <p className="text-white/90 font-medium mt-1">
                    For: {purchase.beneficiary_name}
                  </p>
                )}
                <p className="text-white/70 text-sm mt-2 flex items-center gap-1">
                  <Users className="w-3 h-3" />
                  Organized by {purchase.organizer_name || purchase.organizer_email.split('@')[0]}
                </p>
              </div>
              {daysUntil !== null && (
                <div className="text-right">
                  <div className="text-3xl font-bold">{daysUntil}</div>
                  <div className="text-sm text-white/80">days to go</div>
                </div>
              )}
            </div>
            {/* Date display with edit option for organizers */}
            <div className="flex items-center gap-2 mt-4 text-white/90">
              <Calendar className="w-4 h-4" />
              {purchase.target_date ? (
                <span>{format(new Date(purchase.target_date), "EEEE, MMMM d, yyyy")}</span>
              ) : (
                <span className="text-white/60">No target date set</span>
              )}
              {canEdit && (
                <Popover open={editDateOpen} onOpenChange={setEditDateOpen}>
                  <PopoverTrigger asChild>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="h-6 w-6 p-0 text-white/70 hover:text-white hover:bg-white/10"
                      onClick={() => setEditingDate(purchase.target_date ? new Date(purchase.target_date) : new Date())}
                    >
                      <Pencil className="w-3 h-3" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <div className="p-3">
                      <p className="text-sm font-medium mb-2">Change target date</p>
                      <CalendarPicker
                        mode="single"
                        selected={editingDate}
                        onSelect={setEditingDate}
                        disabled={(date) => date < new Date()}
                        initialFocus
                      />
                      <div className="flex gap-2 mt-3">
                        <Button 
                          size="sm" 
                          variant="outline" 
                          className="flex-1"
                          onClick={() => setEditDateOpen(false)}
                        >
                          Cancel
                        </Button>
                        <Button 
                          size="sm" 
                          className="flex-1 bg-customer hover:bg-customer/90"
                          onClick={handleSaveDate}
                          disabled={isSavingDate || !editingDate}
                        >
                          {isSavingDate ? "Saving..." : "Save"}
                        </Button>
                      </div>
                    </div>
                  </PopoverContent>
                </Popover>
              )}
            </div>
          </div>
          
          {/* Funding Progress Header - GoFundMe Style */}
          {purchase.target_amount && purchase.target_amount > 0 && (
            <div className="px-6 py-4 border-t border-emerald-500/10 bg-background">
              <FundingProgressHeader
                beneficiaryName={purchase.organizer_name}
                targetAmount={purchase.target_amount}
                collectedAmount={purchase.collected_amount || 0}
                currency={purchase.currency || 'INR'}
                paidCount={participants.filter(p => p.status === 'active' && p.has_paid).length}
                pendingCount={participants.filter(p => p.status === 'active' && !p.has_paid).length + 1}
                perPersonAmount={
                  purchase.split_type === 'equal' && purchase.target_amount
                    ? purchase.target_amount / (participants.filter(p => p.status === 'active').length + 1)
                    : null
                }
              />
            </div>
          )}

          {/* Stats Bar */}
          <div className="bg-emerald-500/5 border-t border-emerald-500/10 px-6 py-3 flex items-center gap-4 text-sm text-muted-foreground">
            <span className="flex items-center gap-1">
              <Users className="w-4 h-4" />
              {participants.filter(p => p.status === 'active').length + 1} participants
            </span>
          </div>
        </Card>

        {/* Action Bar */}
        <DiscussionActionBar
          entityType="purchase"
          entityId={id!}
          entityTitle={purchase.title}
          role={isOrganizer ? 'organizer' : isAdmin ? 'admin' : 'participant'}
          themeColor="purple"
          inviteLinkEnabled={purchase.invite_link_enabled || false}
          inviteLinkToken={purchase.invite_link_token || undefined}
          inviteLinkApprovalMode={purchase.invite_link_approval_mode as 'auto' | 'manual'}
          userEmail={userEmail || ''}
          onRefresh={fetchPurchaseData}
          onLeaveSuccess={() => navigate('/my-purchases')}
        />

        {/* Simplified Single View - Payment + Participants */}
        <div className="space-y-4">
          {/* Razorpay Auto Track Payment for Participants */}
          {!isOrganizer && !isAdmin && purchase.razorpay_linked_account_id && (() => {
            const currentUserParticipant = participants.find(
              p => p.email.toLowerCase() === userEmail?.toLowerCase()
            );
            const activeCount = participants.filter(p => p.status === 'active').length;
            const equalShare = purchase.target_amount && activeCount > 0 && purchase.split_type === 'equal'
              ? purchase.target_amount / activeCount
              : 0;
            const platformFee = purchase.platform_fee_per_participant || 10;
            
            if (!currentUserParticipant) return null;
            
            return (
              <Card className="border-primary/30 bg-gradient-to-br from-primary/5 to-primary/10 overflow-hidden">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-medium flex items-center gap-2">
                      <Wallet className="w-4 h-4 text-primary" />
                      Pay Securely via Razorpay
                    </h3>
                    {equalShare > 0 && (
                      <Badge variant="secondary" className="bg-primary/10 text-primary">
                        ₹{equalShare.toFixed(0)} + ₹{platformFee} fee
                      </Badge>
                    )}
                  </div>
                  <RazorpayCheckoutButton
                    purchaseId={purchase.id}
                    participantId={currentUserParticipant.id}
                    participantEmail={currentUserParticipant.email}
                    participantName={currentUserParticipant.name || currentUserParticipant.email.split('@')[0]}
                    amount={equalShare}
                    platformFee={platformFee}
                    currency={purchase.currency || 'INR'}
                    eventName={purchase.title}
                    recipientName={purchase.beneficiary_name || purchase.organizer_name || "the organizer"}
                    onSuccess={fetchPurchaseData}
                    hasPaid={currentUserParticipant.has_paid}
                    disabled={equalShare <= 0}
                  />
                </CardContent>
              </Card>
            );
          })()}

          {/* Manual Payment Section for Participants (only when NOT Auto Track) */}
          {!isOrganizer && !isAdmin && !purchase.razorpay_linked_account_id && (purchase.organizer_upi_id || purchase.payment_link) && (
            <Card className="border-emerald-200 dark:border-emerald-900/30 overflow-hidden">
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-medium flex items-center gap-2">
                    <Wallet className="w-4 h-4 text-emerald-600" />
                    Make Your Payment
                  </h3>
                  {purchase.split_type === 'equal' && purchase.target_amount && (
                    <Badge variant="secondary" className="bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-300">
                      {formatCurrency(
                        purchase.target_amount / (participants.filter(p => p.status === 'active').length + 1),
                        (purchase.currency || 'INR') as Currency
                      )}
                    </Badge>
                  )}
                </div>
                <div className="flex flex-wrap gap-2">
                  {purchase.organizer_upi_id && (
                    <>
                      <Button 
                        size="sm" 
                        className="gap-2 bg-emerald-600 hover:bg-emerald-700"
                        asChild
                      >
                        <a href={`upi://pay?pa=${encodeURIComponent(purchase.organizer_upi_id)}&pn=${encodeURIComponent(purchase.title)}${purchase.split_type === 'equal' && purchase.target_amount ? `&am=${(purchase.target_amount / (participants.filter(p => p.status === 'active').length + 1)).toFixed(2)}` : ''}&cu=${purchase.currency || 'INR'}`}>
                          Pay via UPI App
                        </a>
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline"
                        className="gap-2 border-green-500/30 text-green-700 hover:bg-green-500/10"
                        asChild
                      >
                        <a href={`gpay://upi/pay?pa=${encodeURIComponent(purchase.organizer_upi_id)}&pn=${encodeURIComponent(purchase.title)}${purchase.split_type === 'equal' && purchase.target_amount ? `&am=${(purchase.target_amount / (participants.filter(p => p.status === 'active').length + 1)).toFixed(2)}` : ''}&cu=${purchase.currency || 'INR'}`}>
                          💚 GPay
                        </a>
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline"
                        className="gap-2 border-purple-500/30 text-purple-700 hover:bg-purple-500/10"
                        asChild
                      >
                        <a href={`phonepe://pay?pa=${encodeURIComponent(purchase.organizer_upi_id)}&pn=${encodeURIComponent(purchase.title)}${purchase.split_type === 'equal' && purchase.target_amount ? `&am=${(purchase.target_amount / (participants.filter(p => p.status === 'active').length + 1)).toFixed(2)}` : ''}&cu=${purchase.currency || 'INR'}`}>
                          💜 PhonePe
                        </a>
                      </Button>
                      {/* QR Code Option */}
                      <UpiQrCode
                        upiId={purchase.organizer_upi_id}
                        payeeName={purchase.organizer_name || purchase.title}
                        amount={purchase.split_type === 'equal' && purchase.target_amount
                          ? purchase.target_amount / (participants.filter(p => p.status === 'active').length + 1)
                          : undefined
                        }
                        currency={purchase.currency || 'INR'}
                        transactionNote={`Contribution for ${purchase.title}`}
                      />
                    </>
                  )}
                  {purchase.payment_link && !purchase.organizer_upi_id && (
                    <Button size="sm" className="gap-2 bg-emerald-600 hover:bg-emerald-700" asChild>
                      <a href={purchase.payment_link} target="_blank" rel="noopener noreferrer">
                        <ExternalLink className="w-4 h-4" />
                        {/pay\.google\.com|gpay:/i.test(purchase.payment_link) && 'Pay via GPay'}
                        {/paypal\.me|paypal\.com/i.test(purchase.payment_link) && 'Pay via PayPal'}
                        {/rzp\.io|razorpay\.com/i.test(purchase.payment_link) && 'Pay via Razorpay'}
                        {!/pay\.google\.com|gpay:|paypal\.me|paypal\.com|rzp\.io|razorpay\.com/i.test(purchase.payment_link) && 'Pay Now'}
                      </a>
                    </Button>
                  )}
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  Tap to open your payment app directly
                </p>
              </CardContent>
            </Card>
          )}


          {/* Tabbed Discussion Section - Different for Payments vs Purchases */}
          <Card className={isGroupPayment 
            ? "border-emerald-200/50 dark:border-emerald-900/30" 
            : "border-purple-200/50 dark:border-purple-900/30"
          }>
            {isGroupPayment ? (
              /* Group Payments: Payment Config + Pay Button + People section */
              <div className="space-y-0">
                {/* Payment Configuration for Organizers */}
                {(isOrganizer || isAdmin) && (
                  <div className="border-b border-emerald-200/50 dark:border-emerald-900/30">
                    <GroupPaymentConfig
                      entityType="purchase"
                      entityId={id!}
                      currentConfig={{
                        payment_enabled: true,
                        payment_type: purchase.payment_type as 'none' | 'merchant' | 'organizer',
                        split_type: purchase.split_type as 'equal' | 'custom' | 'open',
                        target_amount: purchase.target_amount,
                        collected_amount: purchase.collected_amount || 0,
                        currency: purchase.currency || 'INR',
                        payment_link: purchase.payment_link,
                        organizer_upi_id: purchase.organizer_upi_id,
                        merchant_id: purchase.merchant_id,
                        razorpay_linked_account_id: purchase.razorpay_linked_account_id,
                        platform_fee_per_participant: purchase.platform_fee_per_participant,
                      }}
                      participantCount={participants.filter(p => p.status === 'active').length + 1}
                      isOrganizer={isOrganizer}
                      onConfigUpdated={fetchPurchaseData}
                      organizerEmail={purchase.organizer_email}
                      pendingInvitationsCount={participants.filter(p => !p.has_paid && p.status === 'active').length}
                      purchaseTitle={purchase.title}
                      organizerName={purchase.organizer_name || undefined}
                    />
                  </div>
                )}

                {/* Pay Your Share Button */}
                {(() => {
                  // Determine if current user should see payment button
                  const paysToBeneficiary = purchase.payment_type === 'merchant';
                  const organizerCollects = purchase.payment_type === 'organizer';
                  
                  // If organizer collects, organizer/admin don't see pay button
                  if (organizerCollects && (isOrganizer || isAdmin)) {
                    return null;
                  }
                  
                  // Find current user - could be organizer (for beneficiary payments) or participant
                  const currentUserParticipant = participants.find(
                    p => p.email.toLowerCase() === userEmail?.toLowerCase()
                  );
                  
                  // For organizer paying beneficiary, we need to check if they're in participants or handle separately
                  const isOrganizerPayingBeneficiary = paysToBeneficiary && (isOrganizer || isAdmin);
                  
                  // Calculate shares - include organizer in count if they're also paying
                  const activeCount = participants.filter(p => p.status === 'active').length;
                  const totalPayers = paysToBeneficiary ? activeCount + 1 : activeCount; // +1 for organizer if they pay too
                  const equalShare = purchase.target_amount && totalPayers > 0 && purchase.split_type === 'equal'
                    ? purchase.target_amount / totalPayers
                    : 0;
                  const platformFee = purchase.platform_fee_per_participant || 10;
                  const isFreeForParticipant = totalPayers <= 10;
                  
                  // For participants, check their record
                  if (!isOrganizerPayingBeneficiary && !currentUserParticipant) {
                    return null;
                  }
                  
                  // Check if user has paid
                  const hasPaid = isOrganizerPayingBeneficiary 
                    ? false // TODO: Track organizer payment separately if needed
                    : currentUserParticipant?.has_paid;
                  
                  // If bank details not configured yet - show placeholder
                  if (!purchase.razorpay_linked_account_id) {
                    return (
                      <div className="p-4 bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-900/30 dark:to-gray-800/20 border-b border-gray-200/50 dark:border-gray-700/30">
                        <div className="flex items-center justify-between mb-3">
                          <h3 className="font-medium flex items-center gap-2 text-muted-foreground">
                            <Wallet className="w-5 h-5" />
                            Payment Coming Soon
                          </h3>
                        </div>
                        <Button disabled className="w-full bg-gray-300 dark:bg-gray-700 cursor-not-allowed">
                          <Clock className="w-4 h-4 mr-2" />
                          You can pay here soon
                        </Button>
                        <p className="text-xs text-muted-foreground mt-2 text-center">
                          The organizer is setting up payment. You'll be able to pay once it's ready.
                        </p>
                      </div>
                    );
                  }
                  
                  return (
                    <div className="p-4 bg-gradient-to-r from-emerald-50 to-teal-50 dark:from-emerald-950/30 dark:to-teal-950/20 border-b border-emerald-200/50 dark:border-emerald-900/30">
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="font-medium flex items-center gap-2 text-emerald-700 dark:text-emerald-300">
                          <Wallet className="w-5 h-5" />
                          Pay Your Share
                        </h3>
                        {equalShare > 0 && (
                          <Badge className="bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-300">
                            ₹{equalShare.toFixed(0)} {!isFreeForParticipant && `+ ₹${platformFee} fee`}
                          </Badge>
                        )}
                      </div>
                      <RazorpayCheckoutButton
                        purchaseId={purchase.id}
                        participantId={isOrganizerPayingBeneficiary ? 'organizer' : currentUserParticipant!.id}
                        participantEmail={userEmail || ''}
                        participantName={userName || userEmail?.split('@')[0] || 'Organizer'}
                        amount={equalShare}
                        platformFee={isFreeForParticipant ? 0 : platformFee}
                        currency={purchase.currency || 'INR'}
                        eventName={purchase.title}
                        recipientName={purchase.beneficiary_name || "the beneficiary"}
                        onSuccess={fetchPurchaseData}
                        hasPaid={hasPaid}
                        disabled={equalShare <= 0}
                      />
                      {/* Fee disclaimer */}
                      <p className="text-xs text-muted-foreground mt-3 text-center">
                        ✨ Free for groups up to 10 participants. {purchase.currency === 'INR' ? '₹10' : '10¢'} per participant for larger groups.
                      </p>
                      <p className="text-xs text-muted-foreground mt-1 text-center">
                        Payments go directly to the beneficiary. We do not collect funds on their behalf.
                      </p>
                      <p className="text-xs text-muted-foreground mt-1 text-center">
                        Standard payment gateway charges will be levied.
                      </p>
                    </div>
                  );
                })()}

                {/* Participants Section Header */}
                <div className="bg-gradient-to-r from-emerald-50 to-emerald-100 dark:from-emerald-950/30 dark:to-emerald-900/20 px-4 py-3 border-b border-emerald-200/50 dark:border-emerald-900/30">
                  <h3 className="font-medium flex items-center gap-2 text-emerald-700 dark:text-emerald-300">
                    <Users className="w-4 h-4" />
                    Participants
                  </h3>
                </div>
                <CardContent className="pt-4">
                  <DiscussionPeople
                    entityType="purchase"
                    entityId={id!}
                    userEmail={userEmail || ''}
                    userName={userName}
                    organizerEmail={purchase.organizer_email}
                    organizerName={purchase.organizer_name}
                    participants={participants.map(p => ({
                      id: p.id,
                      email: p.email,
                      name: p.name,
                      status: p.status,
                      role: p.role,
                      has_paid: p.has_paid,
                      contribution_amount: p.contribution_amount,
                      paid_at: p.paid_at,
                    }))}
                    isOrganizer={isOrganizer}
                    isAdmin={isAdmin}
                    currency={purchase.currency || 'INR'}
                    targetAmount={purchase.target_amount}
                    splitType={purchase.split_type}
                    themeColor="emerald"
                    hasPayments={true}
                    onRefresh={fetchPurchaseData}
                  />
                </CardContent>
                
                {/* Disclaimer for all Group Payment users */}
                <div className="px-4 py-3 bg-emerald-50/50 dark:bg-emerald-950/20 border-t border-emerald-200/50 dark:border-emerald-900/30">
                  <p className="text-xs text-muted-foreground text-center">
                    {(participants.filter(p => p.status === 'active').length + 1) <= 10 ? (
                      <>✨ Free for groups up to 10 participants. No platform fees!</>
                    ) : (
                      <>₹10 platform fee per participant + payment gateway charges apply</>
                    )}
                  </p>
                  <p className="text-xs text-muted-foreground text-center mt-1">
                    Payments go directly to the beneficiary. We do not collect funds on their behalf.
                  </p>
                </div>
              </div>
            ) : (
              /* Group Purchases: Full Ideas, Budget, People tabs */
              <>
              <Tabs defaultValue="ideas" className="w-full">
                <div className="bg-gradient-to-r from-purple-50 to-purple-100 dark:from-purple-950/30 dark:to-purple-900/20 px-4 py-3 border-b border-purple-200/50 dark:border-purple-900/30">
                  <TabsList className="grid w-full grid-cols-3 bg-purple-100/50 dark:bg-purple-900/30">
                    <TabsTrigger value="ideas" className="gap-2">
                      <Lightbulb className="w-4 h-4" />
                      <span className="hidden sm:inline">Ideas</span>
                    </TabsTrigger>
                    <TabsTrigger value="budget" className="gap-2">
                      <DollarSign className="w-4 h-4" />
                      <span className="hidden sm:inline">Budget</span>
                    </TabsTrigger>
                    <TabsTrigger value="people" className="gap-2">
                      <Users className="w-4 h-4" />
                      <span className="hidden sm:inline">People</span>
                    </TabsTrigger>
                  </TabsList>
                </div>
                
                <CardContent className="pt-4">
                  <TabsContent value="ideas" className="mt-0">
                    <DiscussionIdeas
                      entityType="purchase"
                      entityId={id!}
                      userEmail={userEmail || ''}
                      userName={userName}
                      occasion={null}
                      themeColor="purple"
                    />
                  </TabsContent>
                  
                  <TabsContent value="budget" className="mt-0">
                    <DiscussionBudget
                      entityType="purchase"
                      entityId={id!}
                      userEmail={userEmail || ''}
                      participantCount={participants.filter(p => p.status === 'active').length + 1}
                      currency={purchase.currency || 'INR'}
                      targetBudgetMin={purchase.target_budget_min}
                      targetBudgetMax={purchase.target_budget_max}
                      themeColor="purple"
                    />
                  </TabsContent>
                  
                  <TabsContent value="people" className="mt-0">
                    <DiscussionPeople
                      entityType="purchase"
                      entityId={id!}
                      userEmail={userEmail || ''}
                      userName={userName}
                      organizerEmail={purchase.organizer_email}
                      organizerName={purchase.organizer_name}
                      participants={participants.map(p => ({
                        id: p.id,
                        email: p.email,
                        name: p.name,
                        status: p.status,
                        role: p.role,
                        has_paid: p.has_paid,
                        contribution_amount: p.contribution_amount,
                        paid_at: p.paid_at,
                      }))}
                      isOrganizer={isOrganizer}
                      isAdmin={isAdmin}
                      currency={purchase.currency || 'INR'}
                      targetAmount={purchase.target_amount}
                      splitType={purchase.split_type}
                      themeColor="purple"
                      hasPayments={purchase.payment_type !== 'none' && !!purchase.payment_recipient_type}
                      onRefresh={fetchPurchaseData}
                    />
                  </TabsContent>
                </CardContent>
              </Tabs>

              {/* Collect Payment CTA - Only for Organizers/Admins */}
              {canEdit && (
                <div className="m-4 p-4 bg-gradient-to-r from-emerald-500/10 to-teal-500/10 border border-emerald-200 dark:border-emerald-800 rounded-lg">
                  <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center">
                        <CreditCard className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <p className="font-medium text-foreground">Want to collect payment for this purchase?</p>
                        <p className="text-sm text-muted-foreground">Create a group payment to split the cost</p>
                      </div>
                    </div>
                    <Button 
                      onClick={() => setCollectPaymentDialogOpen(true)}
                      className="bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white"
                    >
                      <CreditCard className="w-4 h-4 mr-2" />
                      Collect Payment
                    </Button>
                  </div>
                </div>
              )}
            </>
            )}
          </Card>

          {/* Collect Payment Dialog */}
          <AddGroupPaymentDialog
            open={collectPaymentDialogOpen}
            onOpenChange={setCollectPaymentDialogOpen}
            organizerEmail={purchase.organizer_email}
            organizerName={purchase.organizer_name || undefined}
            prefilledTitle={`Contribute to ${purchase.title}`}
            prefilledParticipants={participants
              .filter(p => p.status === 'active')
              .map(p => ({ email: p.email, name: p.name || p.email.split('@')[0] }))}
          />
        </div>

        {/* Social Share Section */}
        <div className="mt-6">
          <SocialShareSection 
            shareUrl={`${window.location.origin}/purchases/${id}`}
            category="purchase"
          />
        </div>
      </div>
      
      <footer className="fixed bottom-0 left-0 right-0 z-40 border-t border-border/50 bg-card/95 backdrop-blur supports-[backdrop-filter]:bg-card/90">
        <div className="container py-2">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-2 text-xs text-muted-foreground">
            <div className="flex items-center gap-1.5">
              <Gift className="w-3.5 h-3.5 text-customer" />
              <span className="font-semibold text-foreground text-xs">Shakatah</span>
            </div>
            <div className="flex items-center gap-3 flex-wrap justify-center">
              <Link to="/terms-of-service" className="hover:text-foreground transition-colors">Terms</Link>
              <Link to="/privacy-policy" className="hover:text-foreground transition-colors">Privacy</Link>
              <Link to="/refund-policy" className="hover:text-foreground transition-colors">Refunds</Link>
              <a href="/#faq" className="hover:text-foreground transition-colors">FAQ</a>
              <Link to="/contact" className="hover:text-foreground transition-colors">Contact</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
